import { useState, useEffect } from 'react';
import { statusCodesService, StatusCodeDto } from '../../services/statusCodesService';
import { useTranslation } from '../../contexts/I18nProvider';
import type { SupportedLanguage } from '../../i18n';

/**
 * Felles presentasjon av statuskoder (Model A, besluttet 2026-07-02):
 * frontend eier tekst + farge fra én kilde. Tekst hentes via statusCodesService
 * (in-memory cachet, IKKE localStorage). Farge er Tailwind-klasser (temasystemet)
 * og kan derfor ikke ligge i backend.
 *
 * Konsumeres av StatusBadge, datatable-dropdowns (B3.1) og inline-edit-select —
 * samme kilde, format og farger overalt. Erstatter spredte, hardkodede
 * getStatusColor-switcher og ordersService.getOrderLineStatusText m.fl.
 */

// Fargekart for StcTrans LineType (legacy lager-operasjonstyper via customLabels)
const stcTransLineTypeColors: Record<number, string> = {
  0: 'bg-green-100 text-green-700',   // Varemottak
  1: 'bg-orange-100 text-orange-700', // Korreksjon
  2: 'bg-blue-100 text-blue-700',     // Overføring
  3: 'bg-amber-100 text-amber-700',   // Korreksjon (GR)
  4: 'bg-purple-100 text-purple-700', // Lagertelling
};

/** Fargeklasse (Tailwind) for en statusverdi. Ren funksjon — trygg for dropdowns/inline-edit. */
export function statusColor(statusType: number, code: number, hasCustomLabels = false): string {
  if (hasCustomLabels) {
    return stcTransLineTypeColors[code] || 'bg-gray-100 text-gray-700';
  }

  // ST1 — Order/Line/PList prosess-status (delt)
  if (statusType === 1) {
    if (code === 0) return 'bg-gray-100 text-gray-700';
    if (code >= 1 && code <= 10) return 'bg-blue-100 text-blue-700';
    if (code >= 11 && code <= 20) return 'bg-yellow-100 text-yellow-700';
    if (code >= 21 && code <= 30) return 'bg-orange-100 text-orange-700';
    if (code >= 31 && code <= 40) return 'bg-green-100 text-green-700';
    if (code >= 41 && code <= 50) return 'bg-teal-100 text-teal-700';
    if (code >= 51) return 'bg-purple-100 text-purple-700';
  }

  // ST2 — Lock status
  if (statusType === 2) {
    if (code === 0) return 'bg-green-100 text-green-700';
    return 'bg-red-100 text-red-700';
  }

  // ST25 — Intern flytte-transaksjonstype
  if (statusType === 25) {
    if (code <= 10) return 'bg-blue-100 text-blue-700';
    if (code <= 20) return 'bg-teal-100 text-teal-700';
    return 'bg-purple-100 text-purple-700';
  }

  // ST27 — StcTransaction TransType (distinkt farge per type)
  if (statusType === 27) {
    if (code === 10) return 'bg-yellow-100 text-yellow-700'; // Plukkliste
    if (code === 11) return 'bg-cyan-100 text-cyan-700';     // Lageroverføring
    if (code === 12) return 'bg-purple-100 text-purple-700'; // Utlegg
    if (code === 13) return 'bg-teal-100 text-teal-700';     // Påfylling
    if (code === 14) return 'bg-indigo-100 text-indigo-700'; // Optimalisering
    if (code === 15) return 'bg-orange-100 text-orange-700'; // Korreksjon
    if (code === 17) return 'bg-blue-100 text-blue-700';     // Internflytt
    if (code === 20) return 'bg-green-100 text-green-700';   // Varemottak
    if (code === 88) return 'bg-gray-100 text-gray-700';     // Inngående saldo
    if (code === 100) return 'bg-red-100 text-red-700';      // Telling
    return 'bg-blue-100 text-blue-700';
  }

  // ST32 — Varetype
  if (statusType === 32) {
    if (code === 1) return 'bg-gray-100 text-gray-700';
    if (code === 2) return 'bg-blue-100 text-blue-700';
    if (code === 4) return 'bg-orange-100 text-orange-700';
    if (code === 5) return 'bg-teal-100 text-teal-700';
    if (code === 6) return 'bg-purple-100 text-purple-700';
    return 'bg-gray-100 text-gray-700';
  }

  // ST39 — Reservasjonsstatus (0=Aktiv, 1=Frigjort)
  if (statusType === 39) {
    if (code === 0) return 'bg-green-100 text-green-700';
    return 'bg-gray-100 text-gray-500';
  }

  // Default
  if (code === 0) return 'bg-gray-100 text-gray-700';
  return 'bg-blue-100 text-blue-700';
}

/** Enhetlig etikett: «60 - Eksportert», eller bare «60» hvis tekst mangler. */
export function formatStatusLabel(code: number, text?: string | null): string {
  return text ? `${code} - ${text}` : String(code);
}

export interface StatusOption {
  value: number;      // numerisk kode (skrives tilbake)
  text: string;       // ren tekst (aktivt språk via pickStatusText)
  label: string;      // «kode - tekst»
  colorClass: string; // Tailwind-farge
}

/**
 * ENESTE sted som velger språk-kolonne for statustekst. Alt (StatusBadge, dropdowns,
 * inline-edit) går gjennom denne funksjonen. Konvensjon: lng_1=nb, lng_2=en, lng_3=sv.
 * Fallback til lng_1 (alltid utfylt) når valgt språk mangler tekst.
 */
export function pickStatusText(
  dto: Pick<StatusCodeDto, 'lng_1' | 'lng_2' | 'lng_3'>,
  language: SupportedLanguage
): string {
  const preferred = language === 'sv' ? dto.lng_3 : language === 'en' ? dto.lng_2 : dto.lng_1;
  return preferred && preferred.trim() ? preferred : dto.lng_1;
}

/**
 * Enkelt-tekst for en statusverdi på aktivt språk (via pickStatusText). Null mens den
 * lastes / ved feil. Bruker statusCodesService sin in-memory cache.
 */
export function useStatusText(statusType: number, code: number): string | null {
  const { language } = useTranslation();
  const [text, setText] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    statusCodesService
      .getByStatusType(statusType)
      .then(codes => {
        if (cancelled) return;
        const match = codes.find(c => c.statusCode === code);
        setText(match ? pickStatusText(match, language) : null);
      })
      .catch(() => { /* stille — viser numerisk verdi */ });
    return () => { cancelled = true; };
  }, [statusType, code, language]);

  return text;
}

/**
 * Opsjoner for dropdown-filtre og inline-redigering: [{ value, text, label, colorClass }].
 * Samme kilde/format/farger som StatusBadge.
 */
export function useStatusOptions(statusType: number): StatusOption[] {
  const { language } = useTranslation();
  const [options, setOptions] = useState<StatusOption[]>([]);

  useEffect(() => {
    let cancelled = false;
    statusCodesService
      .getByStatusType(statusType)
      .then(codes => {
        if (cancelled) return;
        setOptions(
          codes.map(c => {
            const text = pickStatusText(c, language);
            return {
              value: c.statusCode,
              text,
              label: formatStatusLabel(c.statusCode, text),
              colorClass: statusColor(statusType, c.statusCode),
            };
          })
        );
      })
      .catch(() => { if (!cancelled) setOptions([]); });
    return () => { cancelled = true; };
  }, [statusType, language]);

  return options;
}
